import type { AuthSession, AuthUser } from "../types/authTypes";
import { useQueryClient } from "@tanstack/react-query";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { SignOutButton } from "../components/SignOutButton";
import { LoadingSpinner } from "@/shared/components/ui/LoadingSpinner";
import { useCallback, useEffect, useRef, useState } from "react";
import { setTelemetryUser, trackEvent } from "@/core/telemetry/telemetry";
import { authService } from "../services/authService";
import { AuthContext } from "./AuthContext";

interface AuthProviderProps {
  children: React.ReactNode;
  onIdentityChange?: (userId: string | null) => void;
}

export function AuthProvider({ children, onIdentityChange }: AuthProviderProps) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [session, setSession] = useState<AuthSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [initializationError, setInitializationError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const authIdentity = useRef<{ userId?: string; generation: number }>({ generation: 0 });
  const getAuthGeneration = useCallback(() => authIdentity.current.generation, []);
  const updateAuthIdentity = useCallback((userId?: string) => {
    onIdentityChange?.(userId ?? null);
    setTelemetryUser(userId ?? null);
    if (authIdentity.current.userId === userId) return;
    // Advance before clearing queries so pending mutations cannot repopulate another session's cache.
    authIdentity.current = { userId, generation: authIdentity.current.generation + 1 };
    void queryClient.cancelQueries();
    queryClient.removeQueries();
  }, [queryClient, onIdentityChange]);

  // Check for existing session on mount and listen to auth changes
  useEffect(() => {
    let active = true;
    let authEventReceived = false;
    let unsubscribe: (() => void) | undefined;
    const applySession = (session: AuthSession | null) => {
      if (!active) return;
      updateAuthIdentity(session?.user.id);
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      setInitializationError(null);
    };
    const showError = (error: unknown) => {
      if (active && !authEventReceived) {
        setInitializationError(error instanceof Error ? error.message : "Unable to load your session.");
        setLoading(false);
      }
    };
    // Subscribe before reading so a late initial response cannot replace a newer auth event.
    authService.onAuthStateChange((_event, session) => {
      authEventReceived = true;
      applySession(session);
    }).then((cleanup) => {
      if (!active) {
        cleanup();
        return;
      }
      unsubscribe = cleanup;
      return authService.getSession()
        .then((session) => {
          if (!authEventReceived) applySession(session);
        });
    }).catch(showError);

    return () => {
      active = false;
      unsubscribe?.();
    };
  }, [updateAuthIdentity, attempt]);

  const signIn = async (email: string, password: string) => {
    let result: Awaited<ReturnType<typeof authService.login>>;
    try {
      result = await authService.login({ email, password });
    } catch (error) {
      trackEvent("auth.sign_in_failed", { provider: authService.kind });
      throw error;
    }
    updateAuthIdentity(result.session?.user.id);
    trackEvent("auth.signed_in", { provider: authService.kind });
    setUser(result.user);
    setSession(result.session);
  };

  const signUp = async (email: string, password: string) => {
    const result = await authService.signup({ email, password });
    updateAuthIdentity(result.session?.user.id);
    trackEvent("auth.signed_up", { provider: authService.kind, confirmed: !!result.session });
    setUser(result.session?.user ?? null);
    setSession(result.session);
    return result.session;
  };

  const signOut = async () => {
    await authService.logout();
    trackEvent("auth.signed_out", { provider: authService.kind });
    updateAuthIdentity(undefined);
    setUser(null);
    setSession(null);
  };

  const value = {
    user,
    session,
    loading,
    getAuthGeneration,
    signIn,
    signUp,
    signOut,
  };

  if (initializationError) {
    const retryInitialization = () => {
      setInitializationError(null);
      setLoading(true);
      setAttempt((value) => value + 1);
    };
    return (
      <div className="mx-auto flex min-h-dvh max-w-lg items-center p-4">
        <ErrorState message={initializationError} onRetry={retryInitialization}>
          <SignOutButton signOut={async () => {
            await signOut();
            retryInitialization();
          }} />
        </ErrorState>
      </div>
    );
  }

  if (authService.kind === "host" && loading) {
    return <LoadingSpinner fullScreen />;
  }

  if (authService.kind === "host" && !user) {
    return (
      <div className="mx-auto flex min-h-dvh max-w-lg items-center p-4">
        <ErrorState title="Sign in to Froppio" message="Use the native app to sign in, then retry." onRetry={() => {
          setLoading(true);
          setAttempt(value => value + 1);
        }} />
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
